import { fetchOfferListFilters } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyObject } from 'lib/object/objectUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { LOYALTY_STAY_UPGRADE_SEARCH_FILTER_VALUE } from 'loyalty/constants/loyaltySearchFilters'
import getLoyaltyStayUpgradeBenefitView from 'loyalty/selectors/getLoyaltyStayUpgradeBenefitView'
import { useEffect, useMemo } from 'react'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'

export const emptyFilters: App.OfferListFilterOptions = {
  error: undefined,
  fetching: true,
  filterOrder: {
    amenities: {},
    holidayTypes: {},
  },
  filters: {
    amenities: {},
    bedrooms: {},
    campaigns: {},
    holidayTypes: {},
    offerTypes: {},
    propertyTypes: {},
    luxPlusFeatures: {},
    loyalty: {},
    agentHubFeatures: {},
    paymentOptions: {},
    total: 0,
  },
  orderedFilters: {
    amenities: [],
    holidayTypes: [],
  },
}

interface Options {
  disabled?: boolean

  /**
   * Whether to fetch the offer list or just return existing offer list
   */
  noFetch?: boolean
}

function useOfferListFilters(filters: App.OfferListFilters, options: Options = EmptyObject): App.OfferListFilterOptions {
  const key = getOfferListKey(filters)
  const dispatch = useAppDispatch()
  const dynamicInclusionsFiltering = !!useOptimizelyExperiment(OptimizelyExperiments.searchDynamicInclusionsFiltering, true)

  useEffect(() => {
    if (!options.disabled && !options.noFetch && !filters?.isStream) {
      dispatch(fetchOfferListFilters(filters))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, options])

  const listFilterOptions = useAppSelector(state => state.offer.offerListFilterOptions[key]) ?? emptyFilters

  const hasLoyaltyStayUpgradeBenefit = !!useAppSelector(getLoyaltyStayUpgradeBenefitView)?.availability

  const listFilters = useMemo(() => {
    let finalListFilters = listFilterOptions

    // use newInclusions filter when dynamicInclusionsFiltering experiment is on
    if (dynamicInclusionsFiltering && finalListFilters.filters?.newInclusions) {
      finalListFilters = structuredClone(finalListFilters)
      finalListFilters.filters!.inclusions = finalListFilters.filters!.newInclusions
    }

    if (!hasLoyaltyStayUpgradeBenefit) {
      finalListFilters = structuredClone(finalListFilters)
      delete finalListFilters.filters?.loyalty[LOYALTY_STAY_UPGRADE_SEARCH_FILTER_VALUE]
    }

    return finalListFilters
  }, [listFilterOptions, hasLoyaltyStayUpgradeBenefit, dynamicInclusionsFiltering])

  return listFilters
}

export default useOfferListFilters
